import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { filter } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class NavigationTrackingService {
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly http = inject(HttpClient);

    private readonly baseUrl = `${environment.API_BASE_URL}tracking/event`;

    private initialized = false;
    private lastTrackedUrl: string | null = null;

    init(): void {
        if (this.initialized) return;
        this.initialized = true;

        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                if (!this.authService.isAuthenticated()) return;

                if (this.lastTrackedUrl === event.urlAfterRedirects) return;
                this.lastTrackedUrl = event.urlAfterRedirects;

                this.http.post(this.baseUrl, {
                    type: 'PAGE_VIEW',
                    page: event.urlAfterRedirects,
                    action: 'user_viewed_page',
                    metadata: { navigationSource: 'router' }
                }).subscribe({
                    error: () => console.error('[Tracking] Failed to log page navigation')
                });
            });
    }

    /**
     * Records a visit to an unknown/invalid URL before the wildcard route
     * redirects the user back to the app root. The router's NavigationEnd
     * only exposes the post-redirect URL, so the original (invalid) URL would
     * otherwise be lost. Call this with the URL that was actually requested.
     */
    trackInvalidRoute(originalUrl: string): void {
        if (!this.authService.isAuthenticated()) return;

        // The wildcard component renders a real route, so this same URL is
        // about to fire a NavigationEnd. Mark it as already tracked so the
        // PAGE_VIEW handler skips it and we emit only the dedicated event.
        this.lastTrackedUrl = originalUrl;

        this.http.post(this.baseUrl, {
            type: 'INVALID_ROUTE',
            page: originalUrl,
            action: 'user_hit_invalid_route',
            metadata: { navigationSource: 'router' }
        }).subscribe({
            error: () => console.error('[Tracking] Failed to log invalid route')
        });
    }
}